import { Box, Button, Container, Grid, Typography } from "@mui/material";
import moment from "moment";
import React, { useContext, useEffect, useState } from "react";
import {
  getGameSystems,
  handlePagingAll
} from "../../components/api/APIUtilities";
import {
  BCPCalendarDay,
  BCPSearch,
  BCPSquarePlus
} from "../../components/icons/bcp/BCPIcons";
import InfoContainer from "../../components/layout/InfoContainer";
import { AuthContext } from "../../components/user/BCPAuthProvider";
import { postNotification } from "../../components/utilities/Notifications";
import { isAppMode } from "../../global/AppOverlay";
import { getEnvironmentVariable } from "../../global/environmentVariables";
import { mapData } from "../events/utils/event";
import PlayDashEvents from "./PlayDashboard/PlayDashEvents";
import { useTranslation } from "react-i18next";
// import PlayDashOrganizations from "./PlayDashboard/PlayDashOrganizations";

const searchLimit = 30;
export default function PlayDashboard(props) {
  const { t } = useTranslation("translation", {
    keyPrefix: "pages.dashboards.play"
  });
  const authContext = useContext(AuthContext);
  const appMode = isAppMode();

  const [findEvents, setFindEvents] = useState(null);
  const [myEvents, setMyEvents] = useState(null);

  const [geoLocation, setGeoLocation] = useState(null);
  const [searchValues, setSearchValues] = useState(null);

  const [findEventsLoading, setFindEventsLoading] = useState(true);
  const [myEventsLoading, setMyEventsLoading] = useState(true);

  const [gameSystems, setGameSystems] = useState(null);
  const [gameSystemDict, setGameSystemDict] = useState({});

  const getEventSearchValues = async () => {
    const tempSearchValues = {
      limit: searchLimit,
      startDate: moment()
        .subtract(3, "days")
        .startOf("day")
        .format("YYYY-MM-DD"),
      endDate: moment().add(2, "months").endOf("month").format("YYYY-MM-DD"),
      location: "Any Location",
      distance: 100,
      distanceType: "miles",

      sortType: "Start Date Ascending",
      sortKey: "eventDate",
      sortAscending: true
    };

    try {
      if ("geolocation" in navigator) {
        tempSearchValues.location = "Current Location";
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setGeoLocation(position.coords);
          },
          (err) => {
            tempSearchValues.location = "Any Location";
            setGeoLocation("none");
          }
        );
      } else {
        tempSearchValues.location = "Any Location";
        setGeoLocation("none");
      }
    } catch (err) {
      postNotification("error", err);
      setGeoLocation("none");
      tempSearchValues.location = "Any Location";
    } finally {
      setSearchValues(tempSearchValues);
    }
  };

  const getFindEvents = async (searchValues, location) => {
    const queryStringParameters = {
      ...searchValues
    };

    if (getEnvironmentVariable("SINGLE_GAME_SYSTEM")) {
      queryStringParameters.gameSystemId =
        getEnvironmentVariable("SINGLE_GAME_SYSTEM");
    } else if (getEnvironmentVariable("SINGLE_GAME_MANUFACTURER")) {
      queryStringParameters.gameSystemManufacturer = getEnvironmentVariable(
        "SINGLE_GAME_MANUFACTURER"
      );
    }

    if (queryStringParameters.location === "Current Location" && location) {
      queryStringParameters.location = JSON.stringify({
        distance: queryStringParameters.distance,
        center: {
          lat: location.latitude,
          long: location.longitude
        }
      });
    } else {
      delete queryStringParameters.location;
      delete queryStringParameters.distance;
      delete queryStringParameters.distanceType;
    }

    try {
      const eventData = await handlePagingAll("/events", queryStringParameters);

      setFindEvents(eventData);
    } catch (err) {
      postNotification("error", err);
    } finally {
      setFindEventsLoading(false);
    }
  };

  // Gets my upcoming events
  const getMyEvents = async () => {
    let queryStringParameters = {};
    if (getEnvironmentVariable("SINGLE_GAME_SYSTEM")) {
      queryStringParameters.gameSystemId =
        getEnvironmentVariable("SINGLE_GAME_SYSTEM");
    } else if (getEnvironmentVariable("SINGLE_GAME_MANUFACTURER")) {
      queryStringParameters.gameSystemManufacturer = getEnvironmentVariable(
        "SINGLE_GAME_MANUFACTURER"
      );
    }

    // Current Tab

    try {
      let _gameSystems = gameSystems;
      if (!_gameSystems) {
        _gameSystems = await getGameSystems({
          inclSystem: true
        });
        setGameSystems(_gameSystems);
      }

      let _gameSystemDict = gameSystemDict;
      if (!_gameSystemDict && typeof gameSystemDict === "object") {
        _gameSystemDict = _gameSystems.forEach((gameSystem) => {
          gameSystemDict[gameSystem.id] = gameSystem;
        });
        setGameSystemDict(_gameSystemDict);
      }

      let eventData = await handlePagingAll("/events", {
        ...queryStringParameters,
        limit: searchLimit,
        playerEvents: true,
        toEvents: true,
        eventEndDateFrom: moment().add(-3, "days").format("YYYY-MM-DDTHH:mm")
      });

      eventData = mapData(eventData, gameSystemDict, moment);

      setMyEvents(eventData);
    } catch (err) {
      postNotification("error", err);
    } finally {
      setMyEventsLoading(false);
    }
  };

  useEffect(() => {
    getEventSearchValues();
    getMyEvents();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (geoLocation !== null && searchValues !== null) {
      getFindEvents(searchValues, geoLocation === "none" ? null : geoLocation);
    }
    // eslint-disable-next-line
  }, [geoLocation, searchValues]);

  return (
    <Box width="100%">
      <Container maxWidth="lg" sx={{ pt: 2, pb: 2 }}>
        <Grid container sx={{ p: 2, gap: 2 }}>
          <Grid
            item
            xs={12}
            sx={{
              display: { xs: "block", md: "flex" },
              justifyContent: "space-between"
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography
                  variant="h2"
                  component="h3"
                  fontWeight="700"
                  gutterBottom
                  sx={{ mb: 4 }}
                >
                  {t(
                    authContext?.authState?.identity?.nickname ||
                      authContext?.authState?.identity?.firstName
                      ? "welcomeBack"
                      : "welcomeBackDefault",
                    {
                      name:
                        authContext?.authState?.identity?.nickname ||
                        authContext?.authState?.identity?.firstName,
                      keyPrefix: "pages.dashboards"
                    }
                  )}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <PlayDashEvents
                  events={findEvents}
                  loading={findEventsLoading}
                  gameSystems={gameSystems}
                  title={t("findEvents")}
                  zoom={8}
                  HeaderButton={
                    <Button
                      variant="contained"
                      href="/play/events"
                      startIcon={<BCPSearch />}
                      fullWidth
                    >
                      {t("findEvents")}
                    </Button>
                  }
                  NoResultsPanel={
                    <InfoContainer
                      title={t("noEventsFoundTitle")}
                      body={t("noEventsFoundDescription")}
                      ActionButton={
                        <Button
                          variant="contained"
                          href="/play/events"
                          target={`${appMode ? "_blank" : "_self"}`}
                          startIcon={<BCPSearch />}
                        >
                          {t("searchForEvents")}
                        </Button>
                      }
                    />
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <PlayDashEvents
                  events={myEvents}
                  loading={myEventsLoading}
                  gameSystems={gameSystems}
                  title={t("myUpcomingEvents")}
                  zoom={10}
                  HeaderButton={
                    <Button
                      variant="contained"
                      href="/play/my-events"
                      startIcon={<BCPCalendarDay />}
                      fullWidth
                    >
                      {t("viewAllMyEvents")}
                    </Button>
                  }
                  NoResultsPanel={
                    <InfoContainer
                      title={t("noUpcomingEventsTitle")}
                      body={t("noUpcomingEventsDescription")}
                      ActionButton={
                        <Box
                          sx={{
                            display: "flex",
                            gap: 2,
                            flexWrap: "wrap",
                            justifyContent: "center"
                          }}
                        >
                          <Button
                            variant="contained"
                            href="/play/events"
                            target={`${appMode ? "_blank" : "_self"}`}
                            startIcon={<BCPSearch />}
                          >
                            {t("searchForEvents")}
                          </Button>
                          <Button
                            variant="outlined"
                            href="/organize/event"
                            target={`${appMode ? "_blank" : "_self"}`}
                            startIcon={<BCPSquarePlus />}
                          >
                            {t("createAnEvent")}
                          </Button>
                        </Box>
                      }
                    />
                  }
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
